import React, { useState, useRef, useEffect, useCallback, forwardRef, useImperativeHandle } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import cogoToast from "cogo-toast";
import "./ChatWidget.scss";
import ChatBotIcon from "../../../../assets/icons/chatbot-icon.png";
import { encodeShortPayload } from "../../ShortRedirect";

const API_BASE_URL = (process.env.REACT_APP_BASE_URL || "http://localhost:8000/api/v1") + "/chatbot";
const DEFAULT_USER_ID = "124";


const showToast = (message, type = "error", duration = 4000) => {
  const toast = document.createElement("div");
  toast.className = `toast toast-${type}`;
  toast.textContent = message;
  toast.style.cssText = `
    position: fixed;
    top: 80px;
    right: 20px;
    max-width: 320px;
    background: ${type === "error" ? "#ef4444" : "#10b981"};
    color: white;
    padding: 16px 24px;
    border-radius: 12px;
    z-index: 10000;
    font-size: 15px;
    font-weight: 500;
    line-height: 1.4;
    box-shadow: 0 8px 24px rgba(0,0,0,0.2);
    animation: slideIn 0.3s ease-out;
    white-space: pre-wrap;
    word-break: break-all;
  `;

  // Only add the keyframe style once
  if (!document.getElementById('toast-slidein-style')) {
    const style = document.createElement("style");
    style.id = 'toast-slidein-style';
    style.textContent = `
      @keyframes slideIn {
        from { transform: translateX(100%); opacity: 0; }
        to { transform: translateX(0); opacity: 1; }
      }
    `;
    document.head.appendChild(style);
  }
  document.body.appendChild(toast);

  setTimeout(() => {
    toast.remove();
  }, duration);
};

/* ---------- Storage Helpers ---------- */
const renderMessageWithLinks = (text) => {
  // Match paths like /myjobs, /invite, /wallet, etc.
  const pathRegex = /(?:^|\s)(\/[a-zA-Z0-9\-\/]+)/g;
  const parts = [];
  let lastIndex = 0;
  let match;
  while ((match = pathRegex.exec(text)) !== null) {
    const [fullMatch, path] = match;
    const matchStart = match.index + (fullMatch.length - path.length);
    if (matchStart > lastIndex) {
      parts.push(text.slice(lastIndex, matchStart));
    }
    parts.push({ path });
    lastIndex = matchStart + path.length;
  }
  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }
  return parts;
};

const transformMessagesToHistory = (messages) => {
  return messages
    .filter((msg) => msg.id !== 1)
    .reduce((acc, msg, index, array) => {
      if (msg.from === "user") {
        const botResponse = array.find(
          (m, idx) => idx > index && m.from === "bot"
        );
        if (botResponse) {
          acc.push({
            question: msg.text,
            answer: botResponse.text,
          });
        }
      }
      return acc;
    }, []);
};

const saveChatHistory = (messages, userId) => {
  const chatHistory = transformMessagesToHistory(messages);
  const key = `chat_history_${userId || 'default'}`;
  localStorage.setItem(key, JSON.stringify(chatHistory));
  return chatHistory;
};

const loadChatHistory = (userId) => {
  try {
    const key = `chat_history_${userId || 'default'}`;
    const stored = localStorage.getItem(key);
    if (stored) {
      const chatHistory = JSON.parse(stored);
      return chatHistory.flatMap((item, index) => [
        { id: index * 2 + 2, from: "user", text: item.question },
        { id: index * 2 + 3, from: "bot", text: item.answer },
      ]);
    }
  } catch (error) {
    console.error("Error loading chat history:", error);
  }
  return [];
};

/* ---------- API Helpers ---------- */
const sendEndChat = async (messages, userId = DEFAULT_USER_ID) => {
  const chatHistory = transformMessagesToHistory(messages);
  if (chatHistory.length === 0) return;

  try {
    const response = await fetch(`${API_BASE_URL}/end-chat/${userId}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ chatHistory }),
    });

    if (response.ok) {
      localStorage.removeItem(`chat_history_${userId}`);
      console.log("End chat request successful");
    } else {
      console.error(
        "Failed to send end chat request:",
        response.status,
        await response.text()
      );
    }
  } catch (error) {
    console.error("Error sending end chat request:", error);
  }
};

const sendChatBeacon = (messages, userId = DEFAULT_USER_ID) => {
  const chatHistory = transformMessagesToHistory(messages);
  if (chatHistory.length === 0) return;

  const blob = new Blob([JSON.stringify({ chatHistory })], {
    type: "application/json",
  });
  navigator.sendBeacon(`${API_BASE_URL}/end-chat/${userId}`, blob);
};


const ChatWidget = forwardRef((props, ref) => {
  const history = useHistory();
  const location = useLocation();
  const { userData } = useSelector((store) => store.auth);
  const userId = userData?.id?.toString() || DEFAULT_USER_ID;
  const [open, setOpen] = useState(false);
  const [promptsDisabled, setPromptsDisabled] = useState(false);
  const [togglingPrompts, setTogglingPrompts] = useState(false);
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [features, setFeatures] = useState([]);
  const [featuresLoaded, setFeaturesLoaded] = useState(false);
  const [showFeatures, setShowFeatures] = useState(false);
  const [activePrompt, setActivePrompt] = useState(null);
  const [copiedPromptId, setCopiedPromptId] = useState(null);
  const [isTourActive, setIsTourActive] = useState(false); // Track if tour is showing
  const [messages, setMessages] = useState(() => [
    {
      id: 1,
      from: "bot",
      text: "👋 Welcome to workbrook! I can help you learn about all our features. Pick a feature below or ask me anything:",
    },
    ...loadChatHistory(userData?.id?.toString()),
  ]);
  const lastActivityRef = useRef(Date.now());
  const messagesRef = useRef(messages);
  useEffect(() => { messagesRef.current = messages; }, [messages]);

  const bodyRef = useRef(null);
  const inactivityTimerRef = useRef(null);
  const lastAutoOpenedPromptIdRef = useRef(null);

  const fetchFeatures = useCallback(async () => {
    if (featuresLoaded) return;
    try {
      const res = await fetch(`${API_BASE_URL}/features`);
      if (!res.ok) throw new Error("Failed to fetch features");
      const data = await res.json();
      if (data.features && data.features.length > 0) {
        setFeatures(data.features);
        setFeaturesLoaded(true);
        setShowFeatures(true);
      }
    } catch (err) {
      console.error("Error fetching features:", err);
    }
  }, [featuresLoaded]);

  // Expose method to parent components for auto-showing features
  useImperativeHandle(ref, () => ({
    autoShowFeature: async (featureName) => {
      if (promptsDisabled) return; // Skip tour if user has disabled tips
      let featureList = features;
      if (!featuresLoaded) {
        try {
          const res = await fetch(`${API_BASE_URL}/features`);
          if (!res.ok) throw new Error("Failed to fetch features");
          const data = await res.json();
          if (data.features && data.features.length > 0) {
            setFeatures(data.features);
            setFeaturesLoaded(true);
            featureList = data.features;
          }
        } catch (err) {
          console.error("Error fetching features for tour:", err);
          return;
        }
      }
      const feature = featureList.find(f => f.name === featureName);
      if (feature) {
        setOpen(true);
        setIsTourActive(true); // Mark tour as active
        setActivePrompt(null); // Clear any PRD prompt
        handleFeatureClick(feature, true);
      }
    }
  }), [features, featuresLoaded, fetchFeatures, promptsDisabled]);

  const handleFeatureClick = async (feature, isTour = false) => {
    setShowFeatures(false);
    const userMsg = { id: Date.now(), from: "user", text: feature.name };
    setMessages((prev) => [...prev, userMsg]);
    setIsLoading(true);

    try {
      const url = isTour
        ? `${API_BASE_URL}/features/${feature.id}?is_tour=1`
        : `${API_BASE_URL}/features/${feature.id}`;
      const res = await fetch(url);
      if (!res.ok) throw new Error("Failed to fetch feature details");
      const data = await res.json();
      const infoText = data.info || feature.description || "Information not available.";
      const botMsg = {
        id: Date.now() + 1,
        from: "bot",
        text: infoText,
        isFeatureDetail: true,
      };
      setMessages((prev) => [...prev, botMsg]);
      setIsTourActive(false); // Tour complete, allow PRD prompts again
    } catch (err) {
      const errorMsg = {
        id: Date.now() + 1,
        from: "bot",
        text: "Sorry, I couldn't load that information. Please try again later.",
      };
      setMessages((prev) => [...prev, errorMsg]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleBackToFeatures = () => {
    setShowFeatures(true);
    const botMsg = {
      id: Date.now(),
      from: "bot",
      text: "You can also explore these features:",
    };
    setMessages((prev) => [...prev, botMsg]);
    // Scroll to bottom so features grid is visible
    setTimeout(() => {
      if (bodyRef.current) {
        bodyRef.current.scrollTo({
          top: bodyRef.current.scrollHeight,
          behavior: "smooth",
        });
      }
    }, 100);
  };


  const handleMinimize = () => {
    setOpen(false);
  };

  const handleTogglePrompts = async () => {
    setTogglingPrompts(true);
    try {
      const res = await fetch(`${API_BASE_URL}/prompts/${userId}/toggle-disable`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
      });
      if (res.ok) {
        const data = await res.json();
        setPromptsDisabled(data.prompts_disabled);
        if (data.prompts_disabled) {
          setActivePrompt(null);
          setMessages((prev) => prev.filter((m) => !m.isPrompt));
          cogoToast.success("Tips disabled. You won't see prompts anymore.");
        } else {
          cogoToast.success("Tips enabled. You'll see helpful prompts again.");
        }
      } else {
        cogoToast.error("Failed to update tip settings. Please try again.");
      }
    } catch (err) {
      console.error("Error toggling prompts:", err);
      cogoToast.error("Failed to update tip settings. Please try again.");
    } finally {
      setTogglingPrompts(false);
    }
  };

  // Click outside to minimize
  const chatRef = useRef(null);
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (open && chatRef.current && !chatRef.current.contains(e.target)) {
        const fab = e.target.closest('.wb-fab');
        if (fab) return;
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  // Fetch prompt disable state on mount
  useEffect(() => {
    if (!userId || userId === DEFAULT_USER_ID) return;
    fetch(`${API_BASE_URL}/prompts/${userId}/status`)
      .then((res) => res.json())
      .then((data) => {
        if (data.prompts_disabled !== undefined) {
          setPromptsDisabled(data.prompts_disabled);
        }
      })
      .catch((err) => {
        console.error('[PROMPTS] Failed to fetch prompt status:', err);
      });
  }, [userId]);

  const handleSend = async () => {
    const trimmed = input.trim();
    if (!trimmed || isLoading) return;

    const userMessage = { id: Date.now(), from: "user", text: trimmed };
    setMessages((prev) => [...prev, userMessage]);
    setInput("");
    setIsLoading(true);
    lastActivityRef.current = Date.now();

    // Handle greetings and small talk instantly (no API call)
    const lowerTrimmed = trimmed.toLowerCase();
    const greetings = ["hi", "hello", "hey", "hi there", "hello there", "hey there", "yo", "sup", "good morning", "good afternoon", "good evening", "howdy", "greetings"];
    const smallTalk = ["thanks", "thank you", "thanks!", "ok", "okay", "cool", "nice", "great", "got it", "understood", "makes sense", "awesome", "perfect"];
    const isGreeting = greetings.includes(lowerTrimmed);
    const isSmallTalk = smallTalk.includes(lowerTrimmed);

    if (isGreeting || isSmallTalk) {
      let reply;
      if (isGreeting) {
        reply = "Hi there! 😊 I can help you learn about any workbrook feature. Pick a feature above or ask me a question!";
      } else {
        reply = "You're welcome! Is there anything else you'd like to know? Pick a feature above or ask away 👇";
      }
      // Restore features view if it was hidden
      if (!showFeatures && features.length > 0) {
        setShowFeatures(true);
      }
      const botMessage = { id: Date.now() + 1, from: "bot", text: reply };
      setMessages((prev) => [...prev, botMessage]);
      setIsLoading(false);
      return;
    }

    try {
      const currentChatHistory = saveChatHistory([...messages, userMessage], userId);
      const response = await fetch(`${API_BASE_URL}/answer`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          question: trimmed,
          chatHistory: currentChatHistory,
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      const botMessage = {
        id: Date.now() + 1,
        from: "bot",
        text: data?.answer || "I don't know that yet.",
      };

      setMessages((prev) => [...prev, botMessage]);
      setTimeout(
        () => saveChatHistory([...messages, userMessage, botMessage], userId),
        100
      );
    } catch (error) {
      const errorMsg = {
        id: Date.now() + 1,
        from: "bot",
        text: "Sorry, I'm having trouble responding. Please try again later.",
      };
      setMessages((prev) => [...prev, errorMsg]);
      cogoToast.error(error.message || "Failed to send message");
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // Reset messages when user switches accounts
  const prevUserIdRef = useRef(userId);
  useEffect(() => {
    if (prevUserIdRef.current !== userId) {
      prevUserIdRef.current = userId;
      setMessages([
        {
          id: 1,
          from: "bot",
          text: "👋 Welcome to workbrook! I can help you learn about all our features. Pick a feature below or ask me anything:",
        },
        ...loadChatHistory(userId),
      ]);
      setActivePrompt(null);
      setFeaturesLoaded(false);
    }
  }, [userId]);

  // Fetch features when chat opens
  useEffect(() => {
    if (open && !featuresLoaded) {
      fetchFeatures();
    }
  }, [open, featuresLoaded, fetchFeatures]);

  // Track isTourActive in a ref to avoid effect re-runs
  const isTourActiveRef = useRef(false);
  useEffect(() => {
    isTourActiveRef.current = isTourActive;
  }, [isTourActive]);

  // Track the last prompt that auto-opened the chat to avoid re-opening
  // const lastAutoOpenedPromptIdRef = useRef(null);

  // After tour ends, fetch prompts (backend decides eligibility)
  useEffect(() => {
    const path = location.pathname;
    const isHomePage = path === '/home' || path === '/home/' || path === '/';
    if (!isTourActive && isHomePage && userId && userId !== DEFAULT_USER_ID && !promptsDisabled) {
      const timer = setTimeout(() => {
        if (!isTourActiveRef.current && !promptsDisabled) {
          fetchPrompts();
        }
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [isTourActive, userId, location.pathname, promptsDisabled]);

  // Auto-fetch prompts on mount — backend decides eligibility (no session cooldown)
  useEffect(() => {
    if (!userId || userId === DEFAULT_USER_ID || promptsDisabled) return;
    const path = location.pathname;
    const isHomePage = path === '/home' || path === '/home/' || path === '/';
    if (!isHomePage) return;
    const timer = setTimeout(() => {
      if (!isTourActiveRef.current && !promptsDisabled) {
        fetchPrompts();
      }
    }, 10000);
    return () => clearTimeout(timer);
  }, [userId, location.pathname, promptsDisabled]);

  // Job-view trigger: fetch prompts with job_view context (backend decides eligibility)
  useEffect(() => {
    if (!userId || userId === DEFAULT_USER_ID || promptsDisabled) return;
    const path = location.pathname;
    const isJobDetail = /^\/home\/\d+/.test(path);
    if (isJobDetail) {
      const match = path.match(/^\/home\/(\d+)/);
      const jobId = match ? match[1] : null;
      const timer = setTimeout(() => {
        if (!isTourActiveRef.current && !promptsDisabled) {
          fetchPrompts('job_view', jobId);
        }
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [location.pathname, userId, promptsDisabled]);

  // Auto-open chat when a prompt arrives and chat is closed
  useEffect(() => {
    if (!activePrompt) return;

    const promptAutoOpenKey = activePrompt.id || activePrompt.prompt_key;
    if (
      !open &&
      !isTourActiveRef.current &&
      promptAutoOpenKey &&
      lastAutoOpenedPromptIdRef.current !== promptAutoOpenKey
    ) {
      lastAutoOpenedPromptIdRef.current = promptAutoOpenKey;
      setOpen(true);
    }
  }, [activePrompt, open]);

  const fetchPrompts = async (trigger = null, jobId = null) => {
    if (promptsDisabled) return;
    try {
      let url = `${API_BASE_URL}/prompts/${userId}`;
      const params = [];
      if (trigger) params.push(`trigger=${trigger}`);
      if (jobId) params.push(`job_id=${jobId}`);
      if (params.length) url += '?' + params.join('&');
      console.log('[PROMPTS] Fetching from:', url);
      const res = await fetch(url);
      if (!res.ok) {
        console.log('[PROMPTS] Response not OK:', res.status);
        return;
      }
      const data = await res.json();
      console.log('[PROMPTS] Response data:', data);
      if (data.prompts && data.prompts.length > 0) {
        const p = data.prompts[0];

        // Show typing indicator first
        setIsLoading(true);

        // After 1.5s, replace typing with the actual prompt message
        setTimeout(() => {
          setIsLoading(false);
          const promptMsg = {
            id: Date.now(),
            from: "bot",
            text: p.message,
            isPrompt: true,
            promptId: p.id,
            promptKey: p.prompt_key,
            promptActionLabel: p.action_label,
            promptActionRoute: p.action_route,
            promptJobId: p.job_id || null,
          };
          setMessages((prev) => [...prev, promptMsg]);
          setActivePrompt(p);
        }, 1500);
      } else {
        console.log('[PROMPTS] No eligible prompts returned');
      }
    } catch (err) {
      console.error("Error fetching prompts:", err);
      setIsLoading(false);
    }
  };

  const handleDismissPrompt = async (promptId) => {
    setMessages((prev) => prev.filter((m) => !(m.isPrompt && m.promptId === promptId)));
    setActivePrompt(null);
    try {
      await fetch(`${API_BASE_URL}/prompts/${userId}/dismiss`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ prompt_id: promptId }),
      });
    } catch (err) {
      console.error("Error dismissing prompt:", err);
    }
  };

  const handlePromptAction = (prompt) => {
    // For refer_candidates prompt: build the specific job's referral link and copy to clipboard
    if (prompt?.promptKey === 'refer_candidates' && prompt?.promptJobId) {
      const baseUrl = process.env.REACT_APP_WEB_URL || window.location.origin;
      const refCode = userData?.refCode || '';
      const code = encodeShortPayload({ jobId: prompt.promptJobId, refCode });
      const referralLink = `${baseUrl}/r/${code}`;

      navigator.clipboard.writeText(referralLink).then(() => {
        setCopiedPromptId(prompt.promptId);
      }).catch(() => {
        cogoToast.error('Could not copy link. Please try again.');
      });

      // Don't dismiss the prompt — leave it visible so user can copy again or come back to it
      return;
    }

    // Default: navigate to the prompt's action route
    if (prompt?.promptActionRoute) {
      history.push(prompt.promptActionRoute);
    }
    // Don't dismiss — leave the prompt visible so user can come back to it
    // Don't fetch next prompt — let the pacing handle it naturally
  };

  // Effects - scroll to prompt first, then user message, then bottom
  useEffect(() => {
    if (bodyRef.current) {
      // Priority 1: Scroll to the last prompt message (so user sees it immediately)
      const promptMessages = bodyRef.current.querySelectorAll('.wb-chatbox__prompt-actions');
      const lastPrompt = promptMessages[promptMessages.length - 1];
      if (lastPrompt) {
        lastPrompt.scrollIntoView({ behavior: "smooth", block: "center" });
        return;
      }
      // Priority 2: Find the last user message element and scroll to it
      const userMessages = bodyRef.current.querySelectorAll('.user');
      const lastUserMsg = userMessages[userMessages.length - 1];
      if (lastUserMsg) {
        lastUserMsg.scrollIntoView({ behavior: "smooth", block: "start" });
      } else {
        // No user messages yet (e.g. on open), scroll to bottom
        bodyRef.current.scrollTo({
          top: bodyRef.current.scrollHeight,
          behavior: "smooth",
        });
      }
    }
  }, [messages, open, isLoading]);

  useEffect(() => {
    if (messages.length > 1) {
      saveChatHistory(messages, userId);
    }
  }, [messages, userId]);

  useEffect(() => {
    const handleActivity = () => {
      clearTimeout(inactivityTimerRef.current);
      lastActivityRef.current = Date.now();
    };

    window.addEventListener("mousemove", handleActivity);
    window.addEventListener("keypress", handleActivity);
    window.addEventListener("click", handleActivity);

    return () => {
      window.removeEventListener("mousemove", handleActivity);
      window.removeEventListener("keypress", handleActivity);
      window.removeEventListener("click", handleActivity);
    };
  }, []); // Only attach once on mount — use ref, no setState

  useEffect(() => {
    const handleUnload = () => sendChatBeacon(messagesRef.current, userId);
    const handleVisibilityChange = () => {
      if (document.hidden) sendChatBeacon(messagesRef.current, userId);
    };

    window.addEventListener("beforeunload", handleUnload);
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      window.removeEventListener("beforeunload", handleUnload);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [userId]); // Only re-attach when userId changes, not on every message


  const unreadCount = activePrompt && !open ? 1 : 0;

  return (
    <>
      <aside
        ref={chatRef}
        className={`wb-chatbox ${open ? "open" : ""}`}
        role="dialog"
      >
        <header className="wb-chatbox__header">
          <div className="avatar">
            <img className="chatbot-icon" src={ChatBotIcon} alt="chatbot-icon" />
          </div>
          <div className="info">
            <div className="name">workbrook assistant</div>
            <small>Ask me anything about your account</small>
          </div>
          <div className="wb-chatbox__header-actions">
            <button
              className="wb-chatbox__header-btn"
              onClick={(e) => {
                e.stopPropagation();
                handleMinimize();
              }}
              aria-label="Minimize chat"
            >
              −
            </button>
          </div>
        </header>

        <main ref={bodyRef} className="wb-chatbox__body">
          {messages.map((m) => (
            <div key={m.id} className={m.from}>
              {renderMessageWithLinks(m.text).map((part, i) =>
                typeof part === "string" ? (
                  <React.Fragment key={i}>{part}</React.Fragment>
                ) : (
                  <span
                    key={i}
                    className="wb-chatbox__chat-link"
                    onClick={() => history.push(part.path)}
                  >
                    {part.path}
                  </span>
                )
              )}
              {m.isPrompt && (
                <div className="wb-chatbox__prompt-actions">
                  {m.promptActionLabel && m.promptActionRoute && (
                    <button
                      className="wb-chatbox__prompt-action"
                      onClick={() => handlePromptAction(m)}
                    >
                      {copiedPromptId === m.promptId
                        ? "✓ Referral link copied!"
                        : m.promptActionLabel}
                    </button>
                  )}
                  <button
                    className="wb-chatbox__prompt-dismiss"
                    onClick={() => handleDismissPrompt(m.promptId)}
                  >
                    Maybe later
                  </button>
                </div>
              )}
              {m.isFeatureDetail && (
                <button
                  className="wb-chatbox__back-features"
                  onClick={handleBackToFeatures}
                >
                  ← Back to features
                </button>
              )}
              {m.from === "bot" &&
                m.id === messages[messages.length - 1]?.id &&
                isLoading && <span className="typing-indicator">...</span>}
            </div>
          ))}

          {showFeatures && features.length > 0 && !isLoading && (
            <div className="wb-chatbox__features-grid">
              {messages.length > 1 && (
                <p className="wb-chatbox__features-label">
                  You can also explore these features:
                </p>
              )}
              {features.map((f) => (
                <button
                  key={f.id}
                  className="wb-chatbox__feature-btn"
                  onClick={() => handleFeatureClick(f)}
                >
                  {f.name}
                </button>
              ))}
            </div>
          )}

          {isLoading && (
            <div className="bot">
              <span className="typing-indicator">Assistant is typing...</span>
            </div>
          )}
        </main>

        <footer className="wb-chatbox__footer">
          <textarea
            placeholder="Type your message…"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            disabled={isLoading}
          />
          <button
            onClick={handleSend}
            disabled={!input.trim() || isLoading}
            aria-label="Send message"
          >
            {isLoading ? <span className="sending-indicator">⏳</span> : "➤"}
          </button>
        </footer>

        <div className="wb-chatbox__settings-bar">
          <button
            className={`wb-chatbox__disable-prompts ${promptsDisabled ? "is-disabled" : ""}`}
            onClick={handleTogglePrompts}
            disabled={togglingPrompts}
          >
            {togglingPrompts ? "⏳ Updating..." : promptsDisabled ? "Enable tips" : "Disable tips"}
          </button>
        </div>
      </aside>

      <button
        className="wb-fab"
        aria-label={open ? "Close chat" : "Open chat"}
        onClick={() => setOpen(!open)}
      >
        <img className="chatbot-icon" src={ChatBotIcon} alt="chatbot-icon" />
        {unreadCount > 0 && <span className="badge" />}
      </button>
    </>
  );
});

ChatWidget.displayName = 'ChatWidget';

export default ChatWidget;
